/**
 * GetStartedPage — Auth entry-point with three distinct paths.
 *
 * URL: /get-started
 *
 * Three mutually exclusive flows:
 *   1. Join an existing farm  →  requires invite code  →  /join/:code
 *   2. Start a new farm       →  /signup  (application review queue)
 *   3. Sign in                →  /login
 *
 * Design intent: make it impossible to accidentally stumble into the wrong
 * path. Users who received an invite code should never see a "create a new
 * tenant" flow. Users who want to start a new farm should not be able to
 * browse or search for existing farms to join.
 */
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// ── Design tokens (dark, matches the rest of the app) ─────────────────────────
const C = {
  bg:     'var(--color-bg)',
  card:   'var(--color-bg-card)',
  cardHover: 'var(--color-bg-card-2)',
  border: 'rgba(255,255,255,0.08)',
  borderHover: 'rgba(245,166,35,0.4)',
  amber:  '#F5A623',
  green:  '#2D6A2F',
  text:   'var(--color-text)',
  muted:  'var(--color-text-muted)',
  faint:  'var(--color-text-faint)',
  danger: '#F87171',
}

// ── Mango mark ────────────────────────────────────────────────────────────────
function MangoMark({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 44" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M20 38 C10 38 4 30 4 22 C4 13 10 8 17 7 C19 6 21 6 23 7 C30 9 36 15 36 24 C36 32 29 38 20 38Z"
        fill={C.amber}
      />
      <path
        d="M17 7 C10 8 4 14 4 22 C4 28 7 33 12 36 C8 32 7 26 9 20 C11 13 14 9 17 7Z"
        fill="#E07C12"
        opacity="0.45"
      />
      <ellipse cx="26" cy="15" rx="4" ry="6" fill="rgba(255,255,255,0.22)" transform="rotate(20 26 15)" />
      <path d="M19 7 C18 4 17 1 20 0" stroke={C.green} strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M20 0 C26 -1 28 5 23 6 C21 6 20 3 20 0Z" fill={C.green} />
    </svg>
  )
}

// ── Path card ─────────────────────────────────────────────────────────────────
interface PathCardProps {
  icon: string
  title: string
  description: string
  cta: string
  onClick: () => void
  highlighted?: boolean
}

function PathCard({ icon, title, description, cta, onClick, highlighted }: PathCardProps) {
  const [hovered, setHovered] = useState(false)

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      style={{
        width: '100%',
        background: hovered ? C.cardHover : C.card,
        border: `1px solid ${hovered || highlighted ? C.borderHover : C.border}`,
        borderRadius: 14,
        padding: '24px 24px 20px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background 0.15s, border-color 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <span style={{ fontSize: 32, lineHeight: 1 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{description}</div>
      </div>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 13, fontWeight: 500,
        color: highlighted ? C.amber : (hovered ? C.amber : C.muted),
        transition: 'color 0.15s',
        marginTop: 2,
      }}>
        {cta}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </div>
    </button>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function GetStartedPage() {
  const navigate = useNavigate()
  const [showCodeInput, setShowCodeInput] = useState(false)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const codeInputRef = useRef<HTMLInputElement>(null)

  // Focus the code input when the join-farm panel opens
  useEffect(() => {
    if (showCodeInput) {
      codeInputRef.current?.focus()
    }
  }, [showCodeInput])

  function handleJoinSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = code.trim()
    if (!trimmed) {
      setCodeError('Please enter your invite code.')
      return
    }
    // Invite codes can be any non-empty string — the backend validates
    navigate(`/join/${encodeURIComponent(trimmed)}`)
  }

  return (
    <main
      id="main-content"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: C.bg,
        padding: 24,
      }}
    >
      <div style={{ width: '100%', maxWidth: 440 }}>

        {/* Logo + headline */}
        <header style={{ textAlign: 'center', marginBottom: 40 }}>
          <a
            href="https://grafty.io"
            aria-label="Grafty — go to homepage"
            style={{ textDecoration: 'none', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}
          >
            <MangoMark size={60} />
            <span style={{
              fontSize: 28, fontWeight: 700, color: C.text,
              fontFamily: '"Syne", system-ui, sans-serif', letterSpacing: '-0.02em',
              lineHeight: 1,
            }}>
              Grafty
            </span>
          </a>
          <h1 style={{ fontSize: 18, fontWeight: 500, color: C.text, margin: '16px 0 6px' }}>
            Get started
          </h1>
          <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>
            Every tree, every harvest, every season.
          </p>
        </header>

        {/* Path cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* ── Path 1: Join an existing farm ─────────────────────────────── */}
          {!showCodeInput ? (
            <PathCard
              icon="🌿"
              title="Join an existing farm"
              description="Your farm admin sent you an invite code. Enter it here to accept your invitation."
              cta="Enter invite code"
              onClick={() => { setShowCodeInput(true); setCodeError('') }}
            />
          ) : (
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.borderHover}`,
                borderRadius: 14,
                padding: '24px 24px 20px',
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 10 }}>🌿</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 }}>
                Join an existing farm
              </div>
              <p style={{ fontSize: 13, color: C.muted, margin: '0 0 16px', lineHeight: 1.5 }}>
                Paste the invite code you received from your farm admin.
              </p>
              <form onSubmit={handleJoinSubmit} noValidate>
                <label
                  htmlFor="invite-code"
                  style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                >
                  Invite code
                </label>
                <input
                  ref={codeInputRef}
                  id="invite-code"
                  type="text"
                  value={code}
                  onChange={e => { setCode(e.target.value); setCodeError('') }}
                  placeholder="e.g. abc123"
                  autoComplete="off"
                  aria-describedby={codeError ? 'code-error' : undefined}
                  aria-invalid={!!codeError}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'var(--color-bg-hover)',
                    border: `1px solid ${codeError ? C.danger : C.border}`,
                    borderRadius: 8,
                    padding: '10px 12px',
                    color: C.text,
                    fontSize: 15,
                    letterSpacing: '0.04em',
                    outline: 'none',
                    marginBottom: codeError ? 6 : 14,
                  }}
                />
                {codeError && (
                  <p
                    id="code-error"
                    role="alert"
                    style={{ fontSize: 12, color: C.danger, margin: '0 0 12px', display: 'flex', gap: 4, alignItems: 'center' }}
                  >
                    <span aria-hidden="true">⚠</span> {codeError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="submit"
                    style={{
                      flex: 1, height: 42, background: C.amber, border: 'none', borderRadius: 8,
                      fontSize: 14, fontWeight: 600, color: 'var(--color-bg)', cursor: 'pointer',
                    }}
                  >
                    Continue
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowCodeInput(false); setCode(''); setCodeError('') }}
                    style={{
                      height: 42, padding: '0 16px', background: 'transparent',
                      border: `1px solid ${C.border}`, borderRadius: 8,
                      fontSize: 14, color: C.muted, cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── Path 2: Start a new farm ───────────────────────────────────── */}
          <PathCard
            icon="🌱"
            title="Start a new farm"
            description="Apply to bring your operation onto Grafty. We review every application to ensure a great fit."
            cta="Apply now — it's free"
            onClick={() => navigate('/signup')}
            highlighted
          />

          {/* ── Path 3: Sign in ────────────────────────────────────────────── */}
          <PathCard
            icon="🔑"
            title="Already have an account?"
            description="Sign in to your existing Grafty account to access your farm dashboard."
            cta="Sign in"
            onClick={() => navigate('/login')}
          />
        </div>

        {/* Footer */}
        <p style={{ fontSize: 11, color: C.faint, textAlign: 'center', marginTop: 28, lineHeight: 1.6 }}>
          By continuing, you agree to our{' '}
          <a href="https://grafty.io/terms" style={{ color: C.muted }}>Terms of Service</a>
          {' '}and{' '}
          <a href="https://grafty.io/privacy" style={{ color: C.muted }}>Privacy Policy</a>.
        </p>
      </div>
    </main>
  )
}
